import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaRef } from '@astrally/shared';
import { useApp } from '../state/AppState';
import { blurhashToDataUrl } from '../lib/media';

/**
 * The locked state, and the unlocked one, in a single component.
 *
 * SHARED BY THE FEED AND BY CHAT — it lives in components/ rather than under
 * features/feed for that reason. A second copy for chat would be a second
 * place to get the rule below wrong, and the rule is the entire paywall.
 * Nothing in here is surface-specific: the copy names the media, not the
 * screen it is on.
 *
 * THE WHOLE POINT, because it is easy to reimplement this wrongly: a non-Pro
 * viewer's blurred placeholder is NOT the real image with a CSS blur on it. A
 * client that downloads the file in order to blur it is a client that can keep
 * the original, and a paywall you can defeat with devtools is decoration. So:
 *
 *   - Non-Pro renders `media.blurhash` — about 28 characters that were already
 *     on the post or message document it just read. It makes ZERO network
 *     requests for this media. Nothing to intercept, nothing to cache, no
 *     egress at all.
 *   - Pro fetches the object BY PATH through the backend, which is a
 *     rule-checked request. `storage.rules` refuses it to anyone whose
 *     entitlement document does not say `tier == 'pro'`. That rule, not this
 *     file, is the enforcement.
 *
 * A download URL would break both halves at once (its token bypasses rules for
 * anyone holding the link), which is why `MediaRef.path` is a path.
 *
 * The blur cannot be un-blurred. 28 base-83 characters is ~178 bits — one
 * average colour plus eleven very low-frequency terms — for an image of any
 * size. A face is a smudge and text is a smear. See the BlurHash notes in
 * app/src/lib/media.ts.
 *
 * NOTHING HERE SAYS ANYTHING ABOUT MODERATION, deliberately. Images are
 * checked before they can be referenced; video is checked by nothing at all,
 * and the surface that can carry a video (chat) states that next to the clip.
 * A caveat rendered here would appear on feed photos too and would read as a
 * warning about the wrong thing.
 */

/** Longest edge of the decoded blur bitmap. It is stretched by CSS from here. */
const BLUR_DECODE_PX = 32;

/** Ceiling on how tall one piece of media may be, so a 9:16 clip cannot eat the screen. */
const MAX_MEDIA_HEIGHT = 420;

export function LockedMedia({
  media,
  unlocked,
}: {
  media: MediaRef;
  /** True for Pro viewers, and for the author of the post or message regardless of tier. */
  unlocked: boolean;
}) {
  const { backend } = useApp();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);

  const blurUrl = useMemo(() => {
    const long = Math.max(media.width, media.height) || 1;
    const scale = BLUR_DECODE_PX / long;
    return blurhashToDataUrl(
      media.blurhash,
      Math.max(1, Math.round(media.width * scale)),
      Math.max(1, Math.round(media.height * scale)),
    );
  }, [media.blurhash, media.width, media.height]);

  /**
   * Only fetch what is on screen. A page of fifteen posts at the 300KB image
   * cap is 4.5MB if they all load eagerly — paid once per viewer per page, and
   * mostly for images nobody scrolled to. A long conversation is the same
   * problem with older messages. IntersectionObserver turns that into "what you
   * actually looked at". Where the API is missing (jsdom, very old WebViews)
   * the media is treated as visible, which is the behaviour we had before
   * rather than a blank frame.
   */
  useEffect(() => {
    if (!unlocked || visible) return;
    const node = frameRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      // Start a little before it scrolls in, so the image is there by the time
      // the reader's eye is.
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [unlocked, visible]);

  useEffect(() => {
    if (!unlocked || !visible) return;
    let alive = true;
    let url: string | null = null;
    backend
      .loadMedia(media.path)
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      // Blob URLs are held by the document until revoked; a long session would
      // otherwise accumulate every image it ever scrolled past.
      if (url) URL.revokeObjectURL(url);
    };
  }, [backend, media.path, unlocked, visible]);

  const frame: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: `${media.width} / ${media.height}`,
    maxHeight: MAX_MEDIA_HEIGHT,
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    background: 'var(--card-strong)',
    border: '1px solid var(--border)',
    marginTop: 10,
  };

  const fill: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const seconds = media.durationMs ? Math.max(1, Math.round(media.durationMs / 1000)) : null;

  return (
    <div ref={frameRef} style={frame}>
      {/* The blur is always underneath: it is the placeholder while a Pro
          viewer's fetch is in flight, the fallback when that fetch fails, and
          the entire picture for everyone else. */}
      {blurUrl ? (
        <img
          src={blurUrl}
          alt=""
          aria-hidden
          // The decoded bitmap is 32px on its long edge; the blur smooths the
          // upscale so it reads as deliberate rather than as a broken image.
          style={{ ...fill, filter: 'blur(18px)', transform: 'scale(1.15)' }}
        />
      ) : null}

      {unlocked && objectUrl && media.kind === 'image' && (
        <img src={objectUrl} alt="" style={fill} />
      )}
      {unlocked && objectUrl && media.kind === 'video' && (
        // No autoplay: a screen that starts playing at you is a different
        // product, and every autoplayed second is egress somebody pays for.
        <video src={objectUrl} controls playsInline preload="metadata" style={fill} />
      )}

      {unlocked && !objectUrl && !failed && (
        <div style={overlay}>
          <span className="spinner" />
        </div>
      )}

      {unlocked && failed && (
        <div style={overlay}>
          <div className="tiny" style={{ color: 'var(--text)', textAlign: 'center' }}>
            This didn’t load. Check your connection and try again.
          </div>
        </div>
      )}

      {!unlocked && (
        <div style={overlay}>
          <div style={{ textAlign: 'center', padding: '0 16px', maxWidth: 320 }}>
            <div aria-hidden style={{ fontSize: '1.5rem' }}>
              🔒
            </div>
            <div className="small" style={{ color: 'var(--text)', marginTop: 4 }}>
              {media.kind === 'video' ? `A ${seconds ?? 15}-second video` : 'A photo'}
              {' — Pro members can open it'}
            </div>
            <Link
              to="/profile/subscription"
              className="chip"
              style={{ marginTop: 10, color: 'var(--accent-ink)' }}
            >
              See Astrally Pro →
            </Link>
          </div>
        </div>
      )}

      {/* Duration sits outside the lock overlay so a Pro viewer sees it too. */}
      {unlocked && seconds !== null && (
        <span
          className="tiny"
          style={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            padding: '2px 7px',
            borderRadius: 999,
            background: 'var(--scrim)',
            color: 'var(--text)',
          }}
        >
          {seconds}s
        </span>
      )}
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--scrim)',
};
